<template>
  <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
  <BasePage :back-button="true" pageTitle="Extra Passenger Load" show-menu>
    <div class="d-flex flex-column" style="min-height: 100%; padding-bottom: env(safe-area-inset-bottom, 20px);">
      <!-- Transfer Input -->
      <div class="mb-3 h1">
        <!--<label class="form-label">Coach/Transfer</label>
        <input
            type="text"
            class="form-control"
            placeholder="T123/C001"
            v-model="transferId"
        /> -->
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        {{ __DICT__.transport.transfer }}: {{transferId}}
      </div>

      <!-- Scanned Passengers List -->
      <div class="card mb-3 flex-grow-1" style="min-height: 0">
        <div class="card-header">
          <h3 class="card-title">{{ __DICT__.loadTransferView.scannedPassengers }} ({{ scannedPassengers.length }})</h3>
        </div>
        <div class="card-body p-0" style="overflow-y: auto;">
          <ul class="list-group list-group-flush">
            <li
                v-for="passenger in scannedPassengers"
                :key="passenger.barcode"
                class="list-group-item d-flex justify-content-between align-items-center"
            >
              {{ passenger.barcode }}
              <button
                  class="btn btn-sm btn-danger"
                  @click="confirmDelete(passenger)"
              >
                {{ __DICT__.remove }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Scanner Component -->
      <QrScanner
          v-if="showingScanner"
          @code-scanned="handleScan"
          allow-partial
      />

      <!-- Action Buttons -->
      <div class="action-buttons">
        <div class="d-grid gap-3">
          <button
              class="btn btn-primary"
              @click="toggleScanner"
          >
            {{ showingScanner ? __DICT__.loadTransferView.cancelScan : __DICT__.loadTransferView.scanPassenger }}
          </button>
          <button
              class="btn btn-success"
              @click="submitExtraPassengers"
              :disabled="!canSubmit"
          >
            {{ __DICT__.loadTransferView.submitExtraPassengers }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <AlertModal
        :show="showDeleteModal"
        title="Confirm Removal"
        message="Are you sure you want to remove this passenger?"
        type="danger"
        confirm-text="Remove"
        @close="showDeleteModal = false"
        @confirm="deletePassenger"
    />

    <!-- Success Modal -->
    <AlertModal
        :show="showSuccessModal"
        title="Success"
        message="Extra passengers submitted successfully"
        type="success"
        :show-cancel="false"
        confirm-text="OK"
        @confirm="handleSubmitSuccess"
        @close="showSuccessModal = false"
    />

    <!-- Error Modal -->
    <AlertModal
        :show="!!error"
        title="Error"
        :message="error?.message || 'An error occurred'"
        type="danger"
        :show-cancel="false"
        confirm-text="OK"
        @confirm="error = null"
        @close="error = null"
    />
  </BasePage>
</template>

<script setup>
import {ref, computed, watch, onMounted} from 'vue'
import { useWeekStore } from "@/stores/week.js"
import BasePage from "@/components/BasePage.vue"
import QrScanner from "@/components/QrScanner.vue"
import {qrRequestFields} from "@/utils/qrVerify.js"
import AlertModal from "@/components/AlertModal.vue"
import { api } from "@/services/api.js"
import { decodeBase64Url } from "@/services/base64.js"
import {useRoute, useRouter} from "vue-router";
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";

// Store
const weekStore = useWeekStore()

// Ensure week data is available (handles PWA background recovery)
const { isRecovering } = useEnsureWeekData()
const route = useRoute()
const router = useRouter()
// State
const transferId = ref('')
const showingScanner = ref(false)
const scannedPassengers = ref([])
const showDeleteModal = ref(false)
const showSuccessModal = ref(false)
const error = ref(null)
const passengerToDelete = ref(null)
const submitting = ref(false)
// One key per list, kept across a resend of the same list so the server
// records it once (a new list gets a new key).
let submitKey = null

// Custom debounce function
const debounce = (fn, delay) => {
  let timeoutId
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}

// Load saved passengers from localStorage
const loadSavedPassengers = () => {
  const saved = localStorage.getItem(`extraPassengers-${route.params.transferId}`)
  if (saved) {
    scannedPassengers.value = JSON.parse(saved)
  }
}

// Save passengers to localStorage whenever the array changes
watch(scannedPassengers, (newVal) => {
  submitKey = null
  localStorage.setItem(`extraPassengers-${route.params.transferId}`, JSON.stringify(newVal))
}, { deep: true })

// Computed
const canSubmit = computed(() =>
    transferId.value.trim() && scannedPassengers.value.length > 0 && !submitting.value
)

// Methods
const toggleScanner = () => {
  showingScanner.value = !showingScanner.value
}

// barcode stays the bare reference: it is shown in the list and deduplicated on.
// The token and the scanner's verification travel per passenger beside it.
const addPassenger = (barcode, scan = null) => {
  if (!scannedPassengers.value.some(p => p.barcode === barcode)) {
    scannedPassengers.value.push({
      barcode,
      ...qrRequestFields(scan, barcode),
      timestamp: new Date().toISOString()
    })
  }
}

// Create debounced version of addPassenger
const handleScan = debounce(addPassenger, 500)

const confirmDelete = (passenger) => {
  passengerToDelete.value = passenger
  showDeleteModal.value = true
}

const deletePassenger = () => {
  if (passengerToDelete.value) {
    scannedPassengers.value = scannedPassengers.value.filter(
        p => p.barcode !== passengerToDelete.value.barcode
    )
    passengerToDelete.value = null
  }
  showDeleteModal.value = false
}

const generateGuid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

// The server has the list: clear it from the handset.
const recorded = () => {
  scannedPassengers.value = []
  localStorage.removeItem(`extraPassengers-${route.params.transferId}`)
  showSuccessModal.value = true
}

const submitExtraPassengers = async () => {
  if (submitting.value) return;
  // Guard against null repObj (can happen when PWA resumes from background)
  if (!weekStore.repObj) {
    error.value = { message: 'Session data not available. Please go back and try again.' };
    return;
  }

  submitting.value = true
  submitKey ??= generateGuid()
  try {
    const payload = {
      weekId: weekStore.weekId,
      transferId: transferId.value,
      passengers: scannedPassengers.value,
      repId: weekStore.repObj.repId,
      timestamp: new Date().toISOString(),
      idempotentId: submitKey
    }

    await api.post('/api/transfer/extra-passengers', payload)
    recorded()
  } catch (e) {
    // A resend of a list the server already has (e.g. after a lost response).
    if (e?.code === 'duplicate' && e.original?.outcome === 'accepted') {
      recorded()
    } else if (e?.code === 'duplicate') {
      error.value = { message: 'This list was sent before and its outcome is not known yet. Check with the office before sending it again.' }
    } else {
      error.value = e
    }
  } finally {
    submitting.value = false
  }
}

const handleSubmitSuccess = () => {
  showSuccessModal.value = false
  transferId.value = ''
}

onMounted(() => {
  // Validate transferId parameter exists
  if (!route.params.transferId) {
    console.error('[LoadExtras] Missing transferId parameter, redirecting');
    router.replace({ name: 'selectLoadTransfer' });
    return;
  }

  // Safely decode the transfer parameter
  try {
    transferId.value = decodeBase64Url(route.params.transferId);
  } catch (decodeError) {
    console.error('[LoadExtras] Failed to decode transferId:', decodeError);
    router.replace({ name: 'selectLoadTransfer' });
    return;
  }

  loadSavedPassengers()
})

// Load saved passengers on mount

</script>

<style scoped>
.action-buttons {
  position: sticky;
  bottom: 0;
  background-color: var(--tblr-body-bg, #ffffff);
  padding: 1rem 0;
  margin-top: auto;
  z-index: 1;
}

/* Add padding for iOS safe area */
@supports (padding: max(0px)) {
  .action-buttons {
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
}
</style>